import 'bootstrap-icons/font/bootstrap-icons.css';
import { useEffect, useMemo, useState } from 'react';
import '../css/Logs.css';

import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import useIdleLogout from "../hooks/useIdleLogout";

// API endpoint (env wins; falls back to localhost)
const LOGS_API =
  (process.env.REACT_APP_ADMIN_LOGS_API_URL || '').trim() ||
  'http://192.168.131.228:6060/api/admin-logs';

export default function Logs() {
  useIdleLogout();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');

  // Date range (yyyy-mm-dd)
  const [startDate, setStartDate] = useState(''); // inclusive
  const [endDate, setEndDate] = useState('');     // inclusive

  const token = localStorage.getItem('access_token') || '';

  // Ensure viewport meta exists (prevents iOS zoom and layout bugs on phones)
  useEffect(() => {
    const sel = 'meta[name="viewport"]';
    let m = document.querySelector(sel);
    const content = 'width=device-width, initial-scale=1, viewport-fit=cover';
    if (!m) {
      m = document.createElement('meta');
      m.setAttribute('name', 'viewport');
      m.setAttribute('content', content);
      document.head.appendChild(m);
    } else if (m.content !== content) {
      m.setAttribute('content', content);
    }
  }, []);

  /* -------------------- helpers -------------------- */
  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      timeZone: 'Asia/Thimphu',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const nowThimphuStamp = () => {
    const date = new Date();
    const dPart = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Thimphu' }); // YYYY-MM-DD
    const tPart = date.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Thimphu',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${dPart} ${tPart}`;
  };

  const toISODate = (d) => (d || '').slice(0, 10);

  // Build URL with common param names
  function buildRangeURL(base, start, end) {
    const url = new URL(base, window.location.origin);
    const s = toISODate(start);
    const e = toISODate(end);

    if (s) url.searchParams.set('start_date', s);
    if (e) url.searchParams.set('end_date', e);

    if (s && !url.searchParams.has('start')) url.searchParams.set('start', s);
    if (e && !url.searchParams.has('end')) url.searchParams.set('end', e);

    if (s && !url.searchParams.has('from')) url.searchParams.set('from', s);
    if (e && !url.searchParams.has('to')) url.searchParams.set('to', e);

    return url.toString();
  }

  // Normalize server rows so UI is stable
  function normalize(items) {
    return items.map((it, idx) => ({
      id: it.id ?? it._id ?? it.log_id ?? idx,
      description: (it.description ?? it.activity ?? it.message ?? '').toString().trim(),
      user: (it.user ?? it.admin_name ?? it.username ?? '—').toString(),
      timestamp: it.timestamp ?? it.createdAt ?? it.created_at ?? it.time ?? new Date().toISOString(),
    }));
  }

  // Fetch logs — uses server range if dates chosen; otherwise plain endpoint
  async function fetchLogs() {
    setLoading(true);
    setError('');
    try {
      const headers = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const url =
        startDate || endDate
          ? buildRangeURL(LOGS_API, startDate, endDate)
          : LOGS_API;

      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const json = await res.json();
      const items = Array.isArray(json) ? json : (json.data || []);
      let arr = normalize(items);

      // Client-side fallback filter if backend ignores params
      if (startDate || endDate) {
        const s = startDate ? new Date(startDate + 'T00:00:00+06:00') : null;
        const e = endDate ? new Date(endDate + 'T23:59:59+06:00') : null;
        arr = arr.filter((row) => {
          const t = new Date(row.timestamp);
          if (s && t < s) return false;
          if (e && t > e) return false;
          return true;
        });
      }

      setLogs(arr);
    } catch (e) {
      console.error('Failed to fetch admin logs:', e);
      setError('Could not load logs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  // Initial + refetch when range changes
  useEffect(() => {
    let abort = false;
    (async () => {
      await fetchLogs();
      if (abort) return;
    })();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startDate, endDate]);

  /* -------------------- derived sets -------------------- */
  const validLogs = useMemo(
    () =>
      logs
        .filter((l) => l.description)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    [logs]
  );

  // Text search
  const searched = useMemo(() => {
    if (!query) return validLogs;
    const q = query.toLowerCase();
    return validLogs.filter(
      (log) =>
        log.description.toLowerCase().includes(q) ||
        log.user.toLowerCase().includes(q) ||
        formatTimestamp(log.timestamp).toLowerCase().includes(q)
    );
  }, [validLogs, query]);

  // Show only top 20 on page; keep full set for export
  const displayedLogs = useMemo(() => searched.slice(0, 20), [searched]);

  /* -------------------- Export (FULL set) -------------------- */
  const exportToExcel = async () => {
    await fetchLogs();

    const rangeInfo =
      startDate || endDate
        ? `Activity Logs (${startDate || '…'} to ${endDate || '…'})`
        : 'Activity Logs (All)';
    const exportStamp = `Exported: ${nowThimphuStamp()}`;

    const data = searched.map((log, idx) => ({
      'SI.No': idx + 1,
      Activity: log.description,
      User: log.user,
      'Date & Time': formatTimestamp(log.timestamp),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.sheet_add_aoa(ws, [[rangeInfo]], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [[exportStamp]], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[]], { origin: 'A3' });

    ws['!merges'] = ws['!merges'] || [];
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }); // A1:D1
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 3 } }); // A2:D2

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    const tag = (startDate || endDate)
      ? `${startDate || 'start'}_${endDate || 'end'}`
      : 'all';
    saveAs(new Blob([buf]), `activity-logs-${tag}-${Date.now()}.xlsx`);
  };

  const exportToPDF = async () => {
    await fetchLogs();

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const left = 40;
    const right = 40;

    const rangeInfo =
      startDate || endDate
        ? `Activity Logs (${startDate || '…'} to ${endDate || '…'})`
        : 'Activity Logs (All)';
    const exportStamp = `Exported: ${nowThimphuStamp()}`;

    // ===== Header =====
    doc.setFontSize(16);
    doc.text(rangeInfo, left, 40);

    doc.setFontSize(11);
    const stampWidth = doc.getTextWidth(exportStamp);
    doc.text(exportStamp, pageW - right - stampWidth, 40);

    // ===== Table (improved layout) =====
    autoTable(doc, {
      startY: 60,
      margin: { left, right, bottom: 40 },
      head: [['SI.No', 'Activity', 'User', 'Date & Time']],
      body: searched.map((log, idx) => [
        idx + 1,
        log.description,
        log.user,
        formatTimestamp(log.timestamp),
      ]),
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 4,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [13, 110, 253],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      bodyStyles: {
        textColor: 33,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        0: { cellWidth: 35, halign: 'center' }, // SI.No
        1: { cellWidth: 260 },                  // Activity (wraps)
        2: { cellWidth: 90 },                   // User
        3: { cellWidth: 120 },                  // Date & Time
      },
      showHead: 'everyPage',
      rowPageBreak: 'auto',
    });

    const tag = (startDate || endDate)
      ? `${startDate || 'start'}_${endDate || 'end'}`
      : 'all';
    doc.save(`activity-logs-${tag}-${Date.now()}.pdf`);
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="logs-page container-fluid px-4">
      <div className="logs-topbar-row">
        {/* Search */}
        <div className="logs-search-wrap" style={{ minWidth: 0, width: '100%' }}>
          <input
            type="text"
            className="logs-input search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search activity, user, date…"
            aria-label="Search logs"
            inputMode="search"
            autoComplete="off"
            enterKeyHint="search"
            style={{ fontSize: 16 }}
          />
          <i className="logs-search-icon bi bi-search" />
        </div>

        {/* Date range (labels show on phones, hidden on desktop) */}
        <div className="logs-date-range" style={{ minWidth: 0 }}>
          <div className="date-col">
            <label className="field-label" htmlFor="start-date">Start date</label>
            <input
              id="start-date"
              type="date"
              className="logs-input date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Start date (inclusive)"
              aria-label="Start date (inclusive)"
              style={{ fontSize: 16 }}
            />
          </div>

          <span className="date-sep">to</span>

          <div className="date-col">
            <label className="field-label" htmlFor="end-date">End date</label>
            <input
              id="end-date"
              type="date"
              className="logs-input date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="End date (inclusive)"
              aria-label="End date (inclusive)"
              style={{ fontSize: 16 }}
            />
          </div>
        </div>

        {/* Export */}
        <div className="export-actions">
          <button className="logs-btn" onClick={exportToExcel}>
            Export Excel
          </button>
          <button className="logs-btn pdf" onClick={exportToPDF}>
            Export PDF
          </button>
        </div>
      </div>

      {/* Table (always visible; phone scrolls horizontally) */}
      <div className="card logs-table-card mb-4">
        <div
          className="table-responsive"
          style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            touchAction: 'auto',
          }}
        >
          {loading ? (
            <div className="text-center p-4">Loading logs…</div>
          ) : error ? (
            <div className="text-center text-danger p-4">{error}</div>
          ) : (
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>SI.No</th>
                  <th>Activity</th>
                  <th>User</th>
                  <th>Date &amp; Time</th>
                </tr>
              </thead>
              <tbody>
                {displayedLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No activities to display.
                    </td>
                  </tr>
                ) : (
                  displayedLogs.map((log, i) => (
                    <tr key={log.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{log.description}</td>
                      <td>{log.user}</td>
                      <td title={formatTimestamp(log.timestamp)}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>© {new Date().getFullYear()}  <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </div>
  );
}
